import { z } from 'zod';
/**
 * Permissions matrix — the static RBAC policy `/permissions` renders.
 *
 * Phase 6 of Epic #123 (v7 redesign). Read-only this phase; the mutate
 * path (a Firestore `orgPermissionsPolicy/{orgId}` doc that overrides
 * these defaults) lands later. The matrix is the operator-facing answer
 * to "who can do what in this workspace" — promoted from a flat list of
 * handler-level guards into a single shared source so the page, the
 * server-side guards, and a future override editor all reference the
 * same closed set.
 *
 * Design reference: `docs/designs/v7/project/screens-missing.jsx`
 * (search `PermissionsMatrix`). The four graded access tones — `full`,
 * `self-only`, `with-approval`, `single-dossier`, `none` — come straight
 * from the designer's cell vocabulary.
 *
 * Org roles are already enumerated in `./org.ts` (`OrgRole`); this file
 * doesn't redefine them. When a new role lands, add it to `OrgRole`
 * first, then extend `DEFAULT_PERMISSIONS_POLICY` with the new role's
 * row — keeping the two in sync is what the test below asserts.
 */
import { OrgRole } from './org.js';
/**
 * The 24 capabilities the matrix renders. Each maps to one or more
 * handler-level checks in the API gateway (or, for read-only
 * surface visibility, to a sidebar / route guard). The list is closed;
 * promote new capabilities here before adding the handler check that
 * gates them.
 *
 * Grouped semantically — Read (briefing + audit) / Write (drafts +
 * Rocks + scorecard + content) / Workspace admin (invites + roles +
 * connectors + settings + billing + onboarding + personalization +
 * profile) / Trust (permissions + rules + trust dial + outbound
 * briefing). The 24-count comes straight from the founder intent
 * call-out in `docs/designs/v7/chats/chat2.md` line 1226.
 */
export const PermissionCapability = z.enum([
    // Read
    'view-briefing',
    'view-audit',
    // Write — agent drafts + EOS surfaces + content
    'approve-drafts',
    'mutate-rocks',
    'edit-playbook',
    'mutate-scorecard',
    // Workspace admin
    'invite-members',
    'change-roles',
    'connect-integration',
    'disconnect-integration',
    'export-audit',
    'mutate-settings',
    'mutate-billing',
    'run-onboarding',
    'reset-onboarding',
    'mutate-personalization',
    'mutate-company-profile',
    'mutate-personal-profile',
    // Trust + governance
    'view-permissions',
    'mutate-permissions',
    'view-rules',
    'mutate-rules',
    'override-trust-dial',
    'receive-outbound-briefing',
]);
/**
 * The graduated access tones the matrix's `<AccessPill>` renders.
 *
 *  - `full`             — yes, without restriction. Sage / ✓.
 *  - `self-only`        — yes, only on resources the actor owns.
 *                         Accent.
 *  - `with-approval`    — yes, but a second admin must approve before
 *                         the side effect commits. Accent.
 *  - `single-dossier`   — yes, only within a single explicit-scope
 *                         dossier (guest pattern). Steel.
 *  - `none`             — denied. Rose / em-dash.
 *
 * Tone mapping is the page's responsibility — the schema stays free of
 * presentation tokens so the same constant drives any future
 * server-side guard.
 */
export const AccessGrade = z.enum(['full', 'self-only', 'with-approval', 'single-dossier', 'none']);
/**
 * The static policy the matrix renders. Per-role × per-capability
 * mapping. Indexed by role then capability; partial maps mean
 * unspecified cells default to `'none'` (see `getAccessGrade`).
 *
 * Phase 6 ships this as an exported constant; the write path lands
 * later via a Firestore `orgPermissionsPolicy/{orgId}` doc that
 * overrides per-cell defaults. The fall-through-to-default semantics
 * stay the same so the page renders cleanly with either source.
 *
 * Schema is enum-keyed (`OrgRole` → `PermissionCapability` → grade) so
 * a typo in a future override doc fails Zod parse before the page
 * silently defaults to `'none'`. Zod 4's enum-keyed `z.record` infers
 * a closed `Record<K, V>`, but our policy semantics are
 * fall-through-to-default — we wrap the inferred type in `Partial<>`
 * so consumers can pass sparse overrides like `{ member: {...} }`
 * without the typechecker insisting on every role being present.
 */
const RoleCapabilityGridSchema = z.record(OrgRole, z.record(PermissionCapability, AccessGrade));
export const RoleCapabilityGrid = RoleCapabilityGridSchema;
/**
 * The default policy. Edit here when adding a new role (`OrgRole`) or
 * capability (`PermissionCapability`) — promote first, then add the
 * entry. The test in `permissions.test.ts` enforces that every role
 * has every capability mapped, so a missing entry fails CI rather
 * than silently rendering as `'none'`.
 */
export const DEFAULT_PERMISSIONS_POLICY = {
    [OrgRole.enum.admin]: {
        [PermissionCapability.enum['view-briefing']]: AccessGrade.enum.full,
        [PermissionCapability.enum['view-audit']]: AccessGrade.enum.full,
        [PermissionCapability.enum['approve-drafts']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-rocks']]: AccessGrade.enum.full,
        [PermissionCapability.enum['edit-playbook']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-scorecard']]: AccessGrade.enum.full,
        [PermissionCapability.enum['invite-members']]: AccessGrade.enum.full,
        [PermissionCapability.enum['change-roles']]: AccessGrade.enum.full,
        [PermissionCapability.enum['connect-integration']]: AccessGrade.enum.full,
        [PermissionCapability.enum['disconnect-integration']]: AccessGrade.enum.full,
        [PermissionCapability.enum['export-audit']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-settings']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-billing']]: AccessGrade.enum.full,
        [PermissionCapability.enum['run-onboarding']]: AccessGrade.enum.full,
        [PermissionCapability.enum['reset-onboarding']]: AccessGrade.enum['with-approval'],
        [PermissionCapability.enum['mutate-personalization']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-company-profile']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-personal-profile']]: AccessGrade.enum['self-only'],
        [PermissionCapability.enum['view-permissions']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-permissions']]: AccessGrade.enum['with-approval'],
        [PermissionCapability.enum['view-rules']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-rules']]: AccessGrade.enum.full,
        [PermissionCapability.enum['override-trust-dial']]: AccessGrade.enum.full,
        [PermissionCapability.enum['receive-outbound-briefing']]: AccessGrade.enum.full,
    },
    [OrgRole.enum.member]: {
        [PermissionCapability.enum['view-briefing']]: AccessGrade.enum.full,
        // Member: no audit access today. The audit feed endpoints
        // (`/api/audit/*`) are gated to orgAdmin in `apps/functions/src/http/router.ts`,
        // so advertising `'self-only'` here would mis-state what the
        // server actually does. Follow-up issue tracks the member-scoped
        // audit surface (own-actions only).
        [PermissionCapability.enum['view-audit']]: AccessGrade.enum.none,
        [PermissionCapability.enum['approve-drafts']]: AccessGrade.enum['self-only'],
        [PermissionCapability.enum['mutate-rocks']]: AccessGrade.enum['self-only'],
        [PermissionCapability.enum['edit-playbook']]: AccessGrade.enum.none,
        [PermissionCapability.enum['mutate-scorecard']]: AccessGrade.enum['with-approval'],
        [PermissionCapability.enum['invite-members']]: AccessGrade.enum.none,
        [PermissionCapability.enum['change-roles']]: AccessGrade.enum.none,
        [PermissionCapability.enum['connect-integration']]: AccessGrade.enum['with-approval'],
        [PermissionCapability.enum['disconnect-integration']]: AccessGrade.enum['with-approval'],
        [PermissionCapability.enum['export-audit']]: AccessGrade.enum.none,
        [PermissionCapability.enum['mutate-settings']]: AccessGrade.enum.none,
        [PermissionCapability.enum['mutate-billing']]: AccessGrade.enum.none,
        [PermissionCapability.enum['run-onboarding']]: AccessGrade.enum.full,
        [PermissionCapability.enum['reset-onboarding']]: AccessGrade.enum['self-only'],
        [PermissionCapability.enum['mutate-personalization']]: AccessGrade.enum['self-only'],
        [PermissionCapability.enum['mutate-company-profile']]: AccessGrade.enum.none,
        [PermissionCapability.enum['mutate-personal-profile']]: AccessGrade.enum['self-only'],
        [PermissionCapability.enum['view-permissions']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-permissions']]: AccessGrade.enum.none,
        [PermissionCapability.enum['view-rules']]: AccessGrade.enum.full,
        [PermissionCapability.enum['mutate-rules']]: AccessGrade.enum.none,
        [PermissionCapability.enum['override-trust-dial']]: AccessGrade.enum['with-approval'],
        [PermissionCapability.enum['receive-outbound-briefing']]: AccessGrade.enum.full,
    },
};
/**
 * Pure helper. Returns the grade for a given role + capability,
 * defaulting to `'none'` if the cell is missing from the policy.
 *
 * The default-deny fallback is load-bearing — the override path
 * (Phase-7+) writes partial role maps, and any unspecified cell should
 * read as "denied" rather than crashing the page or leaking a stale
 * default.
 */
export function getAccessGrade(role, capability, policy = DEFAULT_PERMISSIONS_POLICY) {
    // `role` is widened to `string` so the function can serve future
    // unmodeled roles cleanly — narrow it through `OrgRole.safeParse`
    // (no `Record<string, …>` index, no cast). A typo or future role
    // falls through to `'none'`, matching the schema-default semantics.
    const roleParsed = OrgRole.safeParse(role);
    if (!roleParsed.success)
        return AccessGrade.enum.none;
    const grade = policy[roleParsed.data]?.[capability];
    // Validate the cell value through the schema rather than casting —
    // preserves the "no escape-hatch types" rule from CLAUDE.md.
    const parsed = AccessGrade.safeParse(grade);
    return parsed.success ? parsed.data : AccessGrade.enum.none;
}
